import { useEffect } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

import { FaCheckCircle, FaExclamationCircle, FaHandPaper, FaTimes } from "react-icons/fa";

import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import useCommon from "hooks/useCommon";

import { resetSelfAssignState, selfAssignHousekeepingRoom } from "store/slice/housekeepingSlice";

const { primary, dark } = colors;

const ClaimRoomDialog = ({ open, room, onClose }) => {
  const { STORE, DISPATCH } = useCommon();

  const hotelIds = STORE((state) => state.ui.hotelIds);

  const hotelId = hotelIds?.[0];

  const { selfAssignLoading, selfAssignError } = STORE((state) => state.housekeeping);

  useEffect(() => {
    if (!open) return;

    DISPATCH(resetSelfAssignState());
  }, [open, DISPATCH]);

  const handleClose = () => {
    if (selfAssignLoading) {
      return;
    }

    DISPATCH(resetSelfAssignState());

    onClose?.();
  };

  const handleClaimRoom = async () => {
    if (
      !hotelId ||
      !room?.id ||
      selfAssignLoading ||
      room?.version === undefined ||
      room?.version === null
    ) {
      return;
    }

    try {
      await DISPATCH(
        selfAssignHousekeepingRoom({
          roomId: room.id,
          hotelId,
          version: room.version,
        })
      ).unwrap();

      DISPATCH(resetSelfAssignState());

      onClose?.();
    } catch (error) {
      // Error is stored in Redux.
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || selfAssignLoading) {
          return;
        }

        handleClose();
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <VuiBox display="flex" alignItems="center" gap={1}>
            <VuiBox
              width={36}
              height={36}
              borderRadius="10px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                color: primary.main,
                background: rgba(primary.main, 0.12),
                border: `1px solid ${rgba(primary.main, 0.2)}`,
              }}
            >
              <FaHandPaper size={14} />
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="lg">Claim Room</VuiTypography>

              <VuiTypography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.3,
                  opacity: 0.65,
                }}
              >
                {room?.roomName || room?.roomNumber || "Selected room"}
              </VuiTypography>
            </VuiBox>
          </VuiBox>

          <VuiButton
            size="small"
            variant="text"
            color="primary"
            disabled={selfAssignLoading}
            onClick={handleClose}
            sx={{
              minWidth: 34,
              width: 34,
              height: 34,
              px: 0,
              borderRadius: "10px",
            }}
          >
            <FaTimes size={13} />
          </VuiButton>
        </VuiBox>
      </DialogTitle>

      <DialogContent>
        <VuiBox
          p={1.5}
          mt={1}
          borderRadius="12px"
          sx={{
            border: `1px solid ${rgba(dark.main, 0.25)}`,
          }}
        >
          <VuiBox display="flex" alignItems="flex-start" gap={1}>
            <FaCheckCircle
              size={14}
              color={primary.main}
              style={{
                flexShrink: 0,
                marginTop: 2,
              }}
            />

            <VuiTypography
              variant="caption"
              sx={{
                lineHeight: 1.6,
              }}
            >
              Are you sure you want to claim{" "}
              <strong>{room?.roomName || room?.roomNumber || "this room"}</strong>? The room will be
              assigned to you for housekeeping.
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        {selfAssignError && (
          <VuiBox
            display="flex"
            alignItems="center"
            gap={1}
            p={1.2}
            mt={2}
            borderRadius="md"
            sx={{
              border: "1px solid rgba(239,68,68,0.4)",
              background: "rgba(239,68,68,0.08)",
            }}
          >
            <FaExclamationCircle size={14} color="#ef4444" />

            <VuiTypography variant="caption" color="error">
              {typeof selfAssignError === "string"
                ? selfAssignError
                : selfAssignError?.message || "Failed to claim the room"}
            </VuiTypography>
          </VuiBox>
        )}
      </DialogContent>

      <DialogActions sx={{ pb: 2 }}>
        <VuiButton
          variant="outlined"
          color="primary"
          disabled={selfAssignLoading}
          onClick={handleClose}
        >
          Cancel
        </VuiButton>

        <VuiButton
          variant="contained"
          color="primary"
          disabled={selfAssignLoading}
          onClick={handleClaimRoom}
        >
          {selfAssignLoading ? "Claiming..." : "Claim Room"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
};

export default ClaimRoomDialog;
